import { useBranding } from "@/lib/branding";

/**
 * JobGrid word-mark: "J[3x3 grid]BGRID" in Cormorant Garamond serif,
 * with a "No. 1" tagline underneath. Used across all shell headers
 * (dashboard, login, admin, pricing, public-cv, contact) so the brand
 * looks consistent everywhere.
 *
 * When an admin has uploaded a custom PNG via /admin/branding/logo,
 * the <img> variant is rendered instead of the default SVG word-mark.
 */
const SIZE_PRESETS = {
  sm: { letters: "text-lg", cell: 3, gap: 1.5, tagline: "text-[0.55rem]", imgH: 26 },
  md: { letters: "text-xl", cell: 3.5, gap: 1.7, tagline: "text-[0.6rem]",  imgH: 34 },
  lg: { letters: "text-2xl", cell: 4, gap: 2, tagline: "text-[0.7rem]",    imgH: 44 },
};

export default function Logo({ size = "md", showTagline = true, className = "", href = "https://jobgrid.nl" }) {
  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.md;
  const { cell, gap } = preset;
  const grid = cell * 3 + gap * 2;
  const gradId = `jgGold_${size}`;
  const { logoDataUrl } = useBranding();

  return (
    <a
      data-testid="jobgrid-logo"
      href={href}
      className={`inline-flex flex-col items-center leading-none no-underline hover:opacity-80 transition-opacity ${className}`}
    >
      {logoDataUrl ? (
        <img
          src={logoDataUrl}
          alt="JobGrid"
          data-testid="jobgrid-logo-img"
          style={{ maxHeight: preset.imgH, width: "auto" }}
          className="block"
        />
      ) : (
        <span className={`font-serif ${preset.letters} tracking-tight text-neutral-900 inline-flex items-center`}>
          <span>J</span>
          <svg
            role="img"
            aria-label="JobGrid"
            width={grid}
            height={grid}
            viewBox={`0 0 ${grid} ${grid}`}
            className="mx-1 shrink-0"
            style={{ verticalAlign: "middle", transform: `translateY(-${cell * 0.15}px)` }}
          >
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"   stopColor="#D8B76B" />
                <stop offset="50%"  stopColor="#B08D3F" />
                <stop offset="100%" stopColor="#8A6A2A" />
              </linearGradient>
            </defs>
            {[0, 1, 2].flatMap((row) =>
              [0, 1, 2].map((col) => (
                <rect
                  key={`${row}-${col}`}
                  x={col * (cell + gap)}
                  y={row * (cell + gap)}
                  width={cell}
                  height={cell}
                  fill={`url(#${gradId})`}
                />
              ))
            )}
          </svg>
          <span>BGRID</span>
        </span>
      )}
      {showTagline && !logoDataUrl && (
        <span className={`font-serif italic ${preset.tagline} tracking-wide text-neutral-700 mt-0.5`}>
          No.&nbsp;1
        </span>
      )}
    </a>
  );
}
